import { useState, useMemo, useRef, useEffect } from 'react'
import { X, ChevronRight, ChevronLeft, Check, Trash2, Target, TrendingDown, Users } from 'lucide-react'
import { GAME_TYPES, getValidGroupSizes } from '../constants/gameTypes'
import { buildGameHandicapBreakdown, buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { useMatch, useCreateSession, useUpdateSession, useActivateSession } from '../hooks/useMatches'
import { getInitials } from '../utils/colors'
import { useWizardPersistence } from '../hooks/useWizardPersistence'
import StepCourse from './wizard/StepCourse'
import StepGameType from './wizard/StepGameType'
import StepStrokes, { PlayerStrokesTable } from './wizard/StepStrokes'
import StepSideBets from './wizard/StepSideBets'
import FormatSelector from './wizard/FormatSelector'
import Spinner from './Spinner'
import './Modal.css'

const STEPS = ['Course', 'Game Type', 'Matchups', 'Strokes', 'Format', 'Side Games', 'Confirm']

export default function SessionSetupWizard({ matchId, onClose, onSessionCreated, existingSession, skipAnimation }) {
  const { data: match } = useMatch(matchId)
  const createSession = useCreateSession()
  const updateSessionMutation = useUpdateSession()
  const activateSessionMutation = useActivateSession()
  const isEditing = !!existingSession
  const isTeamMatch = match?.match_type === 'team_match'
  const { savedState, saveState: saveWizardState, clearState: clearWizardState } = useWizardPersistence(matchId)

  // Restore persisted session wizard state (only for new sessions, not editing)
  // Version 4 = swapped Matchups before Strokes
  const sw = (!isEditing && savedState?.sw && savedState.sw.version >= 4) ? savedState.sw : {}

  // Pre-populate state from existingSession when editing
  const existingConfig = existingSession?.game_config || {}
  const existingCourseData = existingConfig.courseData || null
  const existingSideBets = existingConfig.sideBets || {}

  const [step, setStep] = useState(sw.step ?? 0)
  const [sessionName, setSessionName] = useState(existingSession?.session_name || sw.sessionName || '')
  const [selectedCourse, setSelectedCourse] = useState(() => {
    if (existingCourseData) {
      return {
        id: existingSession?.course_id || existingCourseData.id,
        name: existingCourseData.name,
        shortName: existingCourseData.shortName,
        holes: existingCourseData.holes,
        par: existingCourseData.par,
        handicapIndex: existingCourseData.handicapIndex,
      }
    }
    if (sw.selectedCourse) return sw.selectedCourse
    return null
  })
  const [selectedTee, setSelectedTee] = useState(() => {
    if (existingCourseData?.tee) return existingCourseData.tee
    if (sw.selectedTee) return sw.selectedTee
    return null
  })
  const [mixedTees, setMixedTees] = useState(() => {
    if (existingCourseData?.mixedTees) return existingCourseData.mixedTees
    if (sw.mixedTees) return sw.mixedTees
    return null
  })
  const [numHoles, setNumHoles] = useState(existingSession?.num_holes || sw.numHoles || 18)
  const [gameTypeId, setGameTypeId] = useState(existingSession?.game_type || sw.gameTypeId || '')
  const [matchFormat, setMatchFormat] = useState(existingConfig.matchFormat || sw.matchFormat || 'nassau')
  const [nassauWeights, setNassauWeights] = useState(existingConfig.nassauWeights || sw.nassauWeights || [1, 1, 1])
  // Matchups: array of { name, order, playerIds }
  const [groups, setGroups] = useState(() => {
    if (existingConfig.groupPlayerAssignments) {
      return existingConfig.groupPlayerAssignments.map((g, i) => {
        const matchingGroup = (existingSession?.session_groups || []).find(sg => sg.group_order === g.groupOrder)
        return {
          name: matchingGroup?.group_name || `Matchup ${i + 1}`,
          order: g.groupOrder,
          playerIds: g.playerIds,
        }
      })
    }
    if (sw.groups) return sw.groups
    return []
  })
  // Per-session scorer designations (player IDs)
  const [scorerPlayerIds, setScorerPlayerIds] = useState(() => {
    if (sw.scorerPlayerIds) return sw.scorerPlayerIds
    if (existingConfig.scorerPlayerIds) return existingConfig.scorerPlayerIds
    return []
  })

  // Side bets config
  const [ctpEnabled, setCtpEnabled] = useState(existingSideBets.ctp?.enabled ?? sw.ctpEnabled ?? false)
  const [ctpAmount, setCtpAmount] = useState(String(existingSideBets.ctp?.amount ?? sw.ctpAmount ?? '5'))
  const [roundLowNetEnabled, setRoundLowNetEnabled] = useState(existingSideBets.lowNet?.enabled ?? sw.roundLowNetEnabled ?? false)
  const [roundLowNetAmount, setRoundLowNetAmount] = useState(String(existingSideBets.lowNet?.amount ?? sw.roundLowNetAmount ?? '10'))
  const [roundSkinsEnabled, setRoundSkinsEnabled] = useState(existingSideBets.skins?.enabled ?? sw.roundSkinsEnabled ?? false)
  const [roundSkinsAmount, setRoundSkinsAmount] = useState(String(existingSideBets.skins?.amount ?? sw.roundSkinsAmount ?? '5'))
  const [roundSkinsCarryOver, setRoundSkinsCarryOver] = useState(existingSideBets.skins?.carryOver ?? sw.roundSkinsCarryOver ?? false)
  const [roundSkinsGrossTiebreaker, setRoundSkinsGrossTiebreaker] = useState(existingSideBets.skins?.grossTiebreaker ?? sw.roundSkinsGrossTiebreaker ?? false)
  const [roundSkinsFullHandicap, setRoundSkinsFullHandicap] = useState(existingSideBets.skins?.fullHandicap ?? sw.roundSkinsFullHandicap ?? false)
  // Stroke mode: 'whs' (default), 'straight_up', or 'custom'
  const [strokeMode, setStrokeMode] = useState(existingConfig.strokeMode ?? sw.strokeMode ?? 'whs')
  const [offLowScope, setOffLowScope] = useState(existingConfig.offLowScope ?? sw.offLowScope ?? 'matchup')
  const [customHandicapPercent, setCustomHandicapPercent] = useState(existingConfig.customHandicapPercent ?? sw.customHandicapPercent ?? null)
  const [customOffLow, setCustomOffLow] = useState(existingConfig.customOffLow ?? sw.customOffLow ?? null)
  const straightUp = strokeMode === 'straight_up'
  const [showCreateConfirm, setShowCreateConfirm] = useState(false)

  // Auto-save session wizard fields on any change (skip initial mount and editing mode)
  const didMount = useRef(false)
  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    if (isEditing) return
    saveWizardState({
      phase: 'session-wizard',
      sw: {
        version: 4,
        step, sessionName, selectedCourse, selectedTee, mixedTees, numHoles,
        gameTypeId, matchFormat, nassauWeights, groups, scorerPlayerIds,
        strokeMode, offLowScope, customHandicapPercent, customOffLow,
        ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount,
        roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker, roundSkinsFullHandicap,
      },
    })
  }, [
    step, sessionName, selectedCourse, selectedTee, mixedTees, numHoles,
    gameTypeId, matchFormat, nassauWeights, groups, scorerPlayerIds,
    strokeMode, offLowScope, customHandicapPercent, customOffLow,
    ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount,
    roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker,
  ])

  const matchPlayers = match?.match_players || []
  const matchTeams = (match?.match_teams || []).sort((a, b) => a.team_order - b.team_order)

  const course = selectedCourse
  const tee = selectedTee
  const courseId = selectedCourse?.id || ''
  const teeName = selectedTee?.name || ''
  const gameType = gameTypeId ? GAME_TYPES.find((g) => g.id === gameTypeId) : null

  // Map player IDs to their team info
  const playerTeamMap = useMemo(() => {
    const map = {}
    matchTeams.forEach((team, idx) => {
      (team.match_team_players || []).forEach(mtp => {
        map[mtp.player_id] = { teamId: team.id, teamName: team.team_name, teamOrder: team.team_order ?? idx }
      })
    })
    return map
  }, [matchTeams])

  // All player IDs across all groups
  const assignedPlayerIds = groups.flatMap(g => g.playerIds)
  const unassignedPlayers = matchPlayers.filter(mp => !assignedPlayerIds.includes(mp.player_id))

  const getPlayerByPlayerId = (playerId) => {
    const mp = matchPlayers.find(mp => mp.player_id === playerId)
    return mp?.player
  }

  // Playing strokes per player for the selected tee + game type.
  // Match play games use the differential method (off-low first, then percentage).
  // Stroke play games apply percentage directly to course handicap.
  const playerStrokesMap = useMemo(() => {
    if (!tee || !gameTypeId) return {}

    const gameConfig = { straightUp, strokeMode, customHandicapPercent, customOffLow, offLowScope }

    // Scramble without custom mode: show per-player course handicaps (team strokes shown separately)
    if (gameTypeId === 'scramble' && strokeMode !== 'custom') {
      const breakdown = buildSideBetHandicapBreakdown({ matchPlayers, course, tee, mixedTees })
      const map = {}
      if (breakdown) {
        breakdown.forEach(p => { map[p.playerId] = p.courseHandicap })
      }
      return map
    }

    // When off-low scope is "matchup", compute per-matchup so each group has its own low player
    if (offLowScope === 'matchup' && groups.length > 0) {
      const map = {}
      for (const group of groups) {
        if (group.playerIds.length === 0) continue
        const groupPlayers = matchPlayers.filter(mp => group.playerIds.includes(mp.player_id))
        const groupStructure = [{ playerIds: group.playerIds }]
        const breakdown = buildGameHandicapBreakdown({
          gameType: gameTypeId, teamStructure: groupStructure, matchPlayers: groupPlayers, course, tee, mixedTees,
          gameConfig: { ...gameConfig, offLowScope: undefined },
        })
        if (breakdown) {
          breakdown.forEach(p => { map[p.playerId] = p.adjustedStrokes })
        }
      }
      return map
    }

    // Session-wide off-low: all players in one group
    const teamStructure = [{ playerIds: matchPlayers.map(mp => mp.player_id) }]
    const breakdown = buildGameHandicapBreakdown({
      gameType: gameTypeId, teamStructure, matchPlayers, course, tee, mixedTees, gameConfig,
    })
    const map = {}
    if (breakdown) {
      breakdown.forEach(p => { map[p.playerId] = p.adjustedStrokes })
    }
    return map
  }, [matchPlayers, tee, mixedTees, course, gameTypeId, straightUp, strokeMode, customHandicapPercent, customOffLow, offLowScope, groups])

  // Scramble: compute team-level handicaps per group, off-low across ALL groups
  // Returns { groupIdx: { teamName: { handicap, strokes } } }
  const scrambleTeamStrokes = useMemo(() => {
    if (gameTypeId !== 'scramble' || !tee || !course) return null

    // Build teams per group using playerTeamMap
    const allGroupTeamStructures = []
    const groupTeams = groups.map((group, gIdx) => {
      const teamsByName = {}
      group.playerIds.forEach(pid => {
        const teamName = playerTeamMap[pid]?.teamName || 'Unknown'
        if (!teamsByName[teamName]) teamsByName[teamName] = []
        teamsByName[teamName].push(pid)
      })
      const teams = Object.entries(teamsByName).map(([name, pids]) => ({ name, pids }))
      teams.forEach(t => allGroupTeamStructures.push(t))
      return { gIdx, teams }
    })

    // Build a single breakdown with all teams across all groups for cross-group off-low
    const allTeamStructure = allGroupTeamStructures.map(t => ({ playerIds: t.pids }))
    const allPlayerIds = new Set(allGroupTeamStructures.flatMap(t => t.pids))
    const relevantPlayers = matchPlayers.filter(mp => allPlayerIds.has(mp.player_id))
    const breakdown = buildGameHandicapBreakdown({
      gameType: 'scramble', teamStructure: allTeamStructure, matchPlayers: relevantPlayers,
      course, tee, mixedTees, gameConfig: { straightUp },
    })

    // Map breakdown back to per-group structure
    // All players on the same team share the same adjustedStrokes in scramble
    const result = {}
    groupTeams.forEach(({ gIdx, teams }) => {
      result[gIdx] = {}
      teams.forEach(t => {
        const teamPlayer = breakdown?.find(p => t.pids.includes(p.playerId))
        result[gIdx][t.name] = {
          handicap: teamPlayer?.adjustedStrokes ?? 0,
          strokes: teamPlayer?.adjustedStrokes ?? 0,
          pids: t.pids,
        }
      })
    })
    return result
  }, [gameTypeId, tee, course, matchPlayers, groups, playerTeamMap])

  // Course handicap map for Matchups step (100% course handicap, no off-low)
  const courseHandicapMap = useMemo(() => {
    if (!tee) return {}
    const breakdown = buildSideBetHandicapBreakdown({ matchPlayers, course, tee, mixedTees })
    const map = {}
    if (breakdown) {
      breakdown.forEach(p => { map[p.playerId] = p.courseHandicap })
    }
    return map
  }, [matchPlayers, course, tee, mixedTees])

  // ---- Navigation ----

  // When game type changes, reset groups (may be invalid for new game)
  const handleGameTypeChange = (newId) => {
    if (newId !== gameTypeId) {
      setGroups([])
      setScorerPlayerIds([])
      setStrokeMode('whs')
      setOffLowScope('matchup')
      setCustomHandicapPercent(null)
      setCustomOffLow(null)
    }
    setGameTypeId(newId)
  }

  const validGroupSizes = useMemo(() => getValidGroupSizes(gameTypeId), [gameTypeId])

  const canAdvance = () => {
    switch (step) {
      case 0: return !!courseId && !!teeName
      case 1: return !!gameTypeId
      case 2: {
        if (groups.length === 0) return false
        if (!groups.every(g => validGroupSizes.includes(g.playerIds.length))) return false
        // Every matchup must have at least 1 designated scorer
        return groups.every(g => g.playerIds.some(pid => scorerPlayerIds.includes(pid)))
      }
      case 3: return true // Strokes always valid (has default)
      case 4: return !!matchFormat
      case 5: return true
      case 6: return true
      default: return false
    }
  }

  const handleNext = () => {
    // Auto-create empty matchups on entry to Matchups step (step 1 → 2)
    // Don't auto-assign players — user needs to mix teams manually
    if (step === 1 && groups.length === 0) {
      const playerCount = matchPlayers.length
      // Cap group size at 4 (a foursome) — no matchup should be larger than a tee time
      const cappedSizes = validGroupSizes.filter(s => s <= 4)
      const maxValid = (cappedSizes.length > 0 ? cappedSizes[cappedSizes.length - 1] : validGroupSizes[0]) || 4
      const bestSize = [...cappedSizes].reverse().find(s => playerCount % s === 0) || maxValid
      const numGroups = Math.max(playerCount > 4 ? 2 : 1, Math.ceil(playerCount / bestSize))
      const newGroups = []
      for (let i = 0; i < numGroups; i++) {
        newGroups.push({ name: `Matchup ${i + 1}`, order: i, playerIds: [] })
      }
      setGroups(newGroups)
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1))
  }

  const handleBack = () => {
    setStep(s => Math.max(s - 1, 0))
  }

  // Android back gesture mirrors the physical Back button in the wizard footer
  useEffect(() => {
    const handler = (e) => {
      e.detail.handled = true
      if (step === 0) {
        onClose()
      } else {
        setStep(s => Math.max(s - 1, 0))
      }
    }
    window.addEventListener('press:back-gesture', handler)
    return () => window.removeEventListener('press:back-gesture', handler)
  }, [step, onClose])

  const handleSubmit = () => {
    // Build game_config with course snapshot
    const gameConfig = {}
    if (course && tee) {
      gameConfig.courseData = {
        id: course.id,
        name: course.name || course.course_name,
        shortName: course.shortName || course.short_name || course.name || course.course_name,
        holes: course.holes,
        par: [...course.par],
        handicapIndex: [...(course.handicapIndex || course.handicap_index || [])],
        tee: {
          name: tee.name,
          rating: tee.rating,
          slope: tee.slope,
          ...(tee.womenRating != null && { womenRating: tee.womenRating }),
          ...(tee.womenSlope != null && { womenSlope: tee.womenSlope }),
          ...(tee.womenHandicapIndex && { womenHandicapIndex: [...tee.womenHandicapIndex] }),
          totalYardage: tee.totalYardage,
          yardages: [...(tee.yardages || [])],
        },
      }
      // Snapshot mixed tees — only players whose tee differs from primary
      if (mixedTees) {
        const diffTees = {}
        for (const [pid, t] of Object.entries(mixedTees)) {
          if (t.name !== tee.name) {
            diffTees[pid] = {
              name: t.name,
              rating: t.rating,
              slope: t.slope,
              ...(t.womenRating != null && { womenRating: t.womenRating }),
              ...(t.womenSlope != null && { womenSlope: t.womenSlope }),
              ...(t.womenHandicapIndex && { womenHandicapIndex: [...t.womenHandicapIndex] }),
              totalYardage: t.totalYardage,
              yardages: [...(t.yardages || [])],
              ...(t.handicapIndex && { handicapIndex: [...t.handicapIndex] }),
            }
          }
        }
        if (Object.keys(diffTees).length > 0) {
          gameConfig.courseData.mixedTees = diffTees
        }
      }
    }
    if (matchFormat) {
      gameConfig.matchFormat = matchFormat
    }
    if (matchFormat === 'nassau' && nassauWeights && nassauWeights.some((w, i) => w !== 1)) {
      gameConfig.nassauWeights = nassauWeights
    }
    gameConfig.groupPlayerAssignments = groups.map(g => ({
      groupOrder: g.order,
      playerIds: g.playerIds,
    }))
    gameConfig.scorerPlayerIds = scorerPlayerIds
    if (strokeMode !== 'whs') gameConfig.strokeMode = strokeMode
    if (strokeMode === 'straight_up') gameConfig.straightUp = true
    if (strokeMode === 'custom') {
      gameConfig.customHandicapPercent = customHandicapPercent
      gameConfig.customOffLow = customOffLow
    }
    if (offLowScope !== 'session') gameConfig.offLowScope = offLowScope
    gameConfig.sideBets = {
      ctp: { enabled: ctpEnabled, amount: parseFloat(ctpAmount) || 5 },
      lowNet: { enabled: roundLowNetEnabled, amount: parseFloat(roundLowNetAmount) || 10 },
      skins: { enabled: roundSkinsEnabled, amount: parseFloat(roundSkinsAmount) || 5, carryOver: roundSkinsCarryOver, grossTiebreaker: roundSkinsGrossTiebreaker, fullHandicap: roundSkinsFullHandicap },
    }

    const sessionPayload = {
      match_id: matchId,
      session_name: sessionName.trim() || (isEditing ? existingSession.session_name : `Session ${(match?.sessions?.length || 0) + 1}`),
      game_type: gameTypeId,
      game_config: gameConfig,
      course_name: course?.name || null,
      course_id: courseId,
      tee_name: teeName,
      num_holes: numHoles,
      press_config: null,
      wager: 0,
      groups: groups.map(g => ({ name: g.name, order: g.order, playerIds: g.playerIds })),
      matchTeams,
    }

    if (isEditing) {
      updateSessionMutation.mutate(
        { ...sessionPayload, id: existingSession.id },
        {
          onSuccess: () => {
            clearWizardState()
            onSessionCreated?.()
          },
        }
      )
    } else {
      createSession.mutate(
        { ...sessionPayload, session_order: match?.sessions?.length || 0 },
        {
          onSuccess: (session) => {
            clearWizardState()
            if (isTeamMatch) {
              // Auto-activate for team match and go straight to scoring
              activateSessionMutation.mutate(
                { id: session.id, match_id: matchId },
                { onSuccess: () => onSessionCreated?.() }
              )
            } else {
              onSessionCreated?.()
            }
          },
        }
      )
    }
  }

  const stepIndicatorContainerRef = useRef(null)

  useEffect(() => {
    const el = stepIndicatorContainerRef.current
    if (!el) return
    const activeStep = el.querySelector('[data-active-step]')
    if (activeStep) {
      const scrollLeft = activeStep.offsetLeft - el.offsetWidth / 2 + activeStep.offsetWidth / 2
      el.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' })
    }
  }, [step])

  const stepIndicator = (
    <div
      ref={stepIndicatorContainerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem 1.25rem',
        gap: '0.25rem',
        borderBottom: '1px solid var(--border-color)',
        marginBottom: '0.5rem',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'var(--bg-primary)',
      }}
      className="hide-scrollbar"
    >
      {STEPS.map((label, i) => (
        <div
          key={label}
          {...(i === step ? { 'data-active-step': true } : {})}
          style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            opacity: i <= step ? 1 : 0.4,
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: i < step ? 'var(--accent-primary)' : i === step ? 'var(--accent-secondary)' : 'var(--bg-tertiary)',
              color: i <= step ? 'white' : 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6875rem',
              fontWeight: '700',
              flexShrink: 0,
            }}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <span style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              color: i === step ? 'var(--text-primary)' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              width: '24px',
              height: '1px',
              backgroundColor: 'var(--border-color)',
              margin: '0 0.25rem',
              flexShrink: 0,
            }} />
          )}
        </div>
      ))}
    </div>
  )

  const stepContent = (
    <div className="modal-form" style={{ flex: 1, overflowY: 'auto' }}>
      {step === 0 && (
        <StepCourse
          selectedCourse={selectedCourse}
          onSelectCourse={(c) => { setSelectedCourse(c); setSelectedTee(null); setMixedTees(null) }}
          selectedTee={selectedTee}
          onSelectTee={(t) => { setSelectedTee(t); setMixedTees(null) }}
          numHoles={numHoles}
          setNumHoles={setNumHoles}
          matchPlayers={matchPlayers}
          mixedTees={mixedTees}
          setMixedTees={setMixedTees}
          courseTees={selectedCourse?.tees}
          nameLabel="Session Name (optional)"
          namePlaceholder="e.g. Day 1 Morning"
          nameValue={sessionName}
          onNameChange={setSessionName}
        />
      )}
      {step === 1 && (
        <StepGameType
          gameTypeId={gameTypeId}
          setGameTypeId={handleGameTypeChange}
          playerCount={0}
          filterFn={isTeamMatch ? (gt => gt.scoring === 'nassau') : undefined}
        />
      )}
      {step === 2 && (
        <StepGroups
          groups={groups}
          setGroups={setGroups}
          unassignedPlayers={unassignedPlayers}
          matchPlayers={matchPlayers}
          playerTeamMap={playerTeamMap}
          getPlayerByPlayerId={getPlayerByPlayerId}
          gameTypeId={gameTypeId}
          courseHandicapMap={courseHandicapMap}
          scorerPlayerIds={scorerPlayerIds}
          setScorerPlayerIds={setScorerPlayerIds}
        />
      )}
      {step === 3 && (
        <StepStrokes
          gameTypeId={gameTypeId}
          strokeMode={strokeMode}
          setStrokeMode={setStrokeMode}
          offLowScope={offLowScope}
          setOffLowScope={setOffLowScope}
          customHandicapPercent={customHandicapPercent}
          setCustomHandicapPercent={setCustomHandicapPercent}
          customOffLow={customOffLow}
          setCustomOffLow={setCustomOffLow}
          showOffLowScope={true}
          playerStrokesMap={playerStrokesMap}
          scrambleTeamStrokes={scrambleTeamStrokes}
          groups={groups}
          matchPlayers={matchPlayers}
          getPlayerByPlayerId={getPlayerByPlayerId}
          playerTeamMap={playerTeamMap}
          courseHandicapMap={courseHandicapMap}
        />
      )}
      {step === 4 && (
        <div>
          <FormatSelector
            matchFormat={matchFormat}
            setMatchFormat={setMatchFormat}
            nassauWeights={nassauWeights}
            setNassauWeights={setNassauWeights}
            numHoles={numHoles}
            gameTypeId={gameTypeId}
          />
        </div>
      )}
      {step === 5 && (
        <StepSideBets
          gameTypeId={gameTypeId}
          course={course}
          ctpEnabled={ctpEnabled}
          setCtpEnabled={setCtpEnabled}
          ctpAmount={ctpAmount}
          setCtpAmount={setCtpAmount}
          roundLowNetEnabled={roundLowNetEnabled}
          setRoundLowNetEnabled={setRoundLowNetEnabled}
          roundLowNetAmount={roundLowNetAmount}
          setRoundLowNetAmount={setRoundLowNetAmount}
          roundSkinsEnabled={roundSkinsEnabled}
          setRoundSkinsEnabled={setRoundSkinsEnabled}
          roundSkinsAmount={roundSkinsAmount}
          setRoundSkinsAmount={setRoundSkinsAmount}
          roundSkinsCarryOver={roundSkinsCarryOver}
          setRoundSkinsCarryOver={setRoundSkinsCarryOver}
          roundSkinsGrossTiebreaker={roundSkinsGrossTiebreaker}
          setRoundSkinsGrossTiebreaker={setRoundSkinsGrossTiebreaker}
          roundSkinsFullHandicap={roundSkinsFullHandicap}
          setRoundSkinsFullHandicap={setRoundSkinsFullHandicap}
          lowNetLabel="session"
        />
      )}
      {step === 6 && (
        <StepConfirm
          course={course}
          tee={tee}
          numHoles={numHoles}
          gameType={gameType}
          sessionName={sessionName}
          groups={groups}
          matchPlayers={matchPlayers}
          playerTeamMap={playerTeamMap}
          getPlayerByPlayerId={getPlayerByPlayerId}
          ctpEnabled={ctpEnabled}
          ctpAmount={ctpAmount}
          roundLowNetEnabled={roundLowNetEnabled}
          roundLowNetAmount={roundLowNetAmount}
          roundSkinsEnabled={roundSkinsEnabled}
          roundSkinsAmount={roundSkinsAmount}
          roundSkinsCarryOver={roundSkinsCarryOver}
          roundSkinsGrossTiebreaker={roundSkinsGrossTiebreaker}
          playerStrokesMap={playerStrokesMap}
          scrambleTeamStrokes={scrambleTeamStrokes}
          courseHandicapMap={courseHandicapMap}
          onEditMatchups={() => setStep(2)}
        />
      )}
    </div>
  )

  const actions = (
    <div style={{
      position: 'fixed',
      bottom: 'var(--keyboard-height, 0px)',
      left: 0,
      right: 0,
      display: 'flex',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      paddingBottom: 'var(--action-bar-pad-bottom)',
      backgroundColor: 'var(--bg-primary)',
      borderTop: '1px solid var(--border-color)',
      zIndex: 1001,
    }}>
      {step > 0 ? (
        <button type="button" className="btn-secondary btn-action" onClick={handleBack}>
          <ChevronLeft size={18} />
          Back
        </button>
      ) : (
        <button type="button" className="btn-secondary btn-action" onClick={onClose}>
          Back
        </button>
      )}
      {step < STEPS.length - 1 ? (
        <button
          type="button"
          className="btn-primary btn-action"
          onClick={handleNext}
          disabled={!canAdvance()}
          style={{ opacity: canAdvance() ? 1 : 0.5 }}
        >
          Next
          <ChevronRight size={18} />
        </button>
      ) : (
        <button
          type="button"
          className="btn-primary btn-action"
          onClick={() => setShowCreateConfirm(true)}
        >
          {isEditing ? 'Save Changes' : 'Create Session'}
        </button>
      )}
    </div>
  )

  const isSaving = isEditing ? updateSessionMutation.isPending : (createSession.isPending || activateSessionMutation.isPending)

  const confirmModal = showCreateConfirm && (
    <div
      onClick={isSaving ? undefined : () => setShowCreateConfirm(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '1rem',
          border: '1px solid var(--border-color)',
          padding: '1.5rem',
          maxWidth: '340px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        }}
      >
        {isSaving ? (
          <Spinner message={isEditing ? 'Saving changes...' : 'Starting session...'} />
        ) : (
          <>
            <p style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem', textAlign: 'center' }}>
              {isEditing ? 'Save Changes?' : 'Create Session?'}
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: '1.25rem', textAlign: 'center' }}>
              {isEditing
                ? `This will update the session with ${groups.length} matchup${groups.length !== 1 ? 's' : ''}.`
                : `This will create the session with ${groups.length} matchup${groups.length !== 1 ? 's' : ''}. You can start scoring after creation.`
              }
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={handleSubmit}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  color: '#fff',
                  backgroundColor: 'var(--accent-primary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {isEditing ? 'Confirm Save' : 'Confirm Create Session'}
              </button>
              <button
                onClick={() => setShowCreateConfirm(false)}
                style={{
                  height: '44px',
                  width: '100%',
                  fontSize: '0.9375rem',
                  borderRadius: '9999px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )

  return (
    <div className="modal-overlay" onClick={onClose} style={skipAnimation ? { animation: 'none' } : undefined}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={skipAnimation ? { paddingBottom: '5rem', animation: 'none' } : { paddingBottom: '5rem' }}>
        {/* Sticky header + step indicator group */}
        <div style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--bg-primary)' }}>
          <div className="modal-header" style={{ position: 'static' }}>
            <h2>{isEditing ? 'Edit Session' : 'Session Setup'}</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          {stepIndicator}
        </div>
        {stepContent}
        {actions}
      </div>
      {confirmModal}
    </div>
  )
}

// Step 3: Matchups (unique to sessions)
// ============================================
function StepGroups({ groups, setGroups, unassignedPlayers, matchPlayers, playerTeamMap, getPlayerByPlayerId, gameTypeId, courseHandicapMap, scorerPlayerIds, setScorerPlayerIds }) {
  const validSizes = useMemo(() => getValidGroupSizes(gameTypeId), [gameTypeId])
  const [addingToGroup, setAddingToGroup] = useState(null)

  const addGroup = () => {
    setGroups(prev => [...prev, { name: `Matchup ${prev.length + 1}`, order: prev.length, playerIds: [] }])
  }

  const removeGroup = (index) => {
    setGroups(prev => prev.filter((_, i) => i !== index).map((g, i) => ({ ...g, order: i })))
    if (addingToGroup === index) setAddingToGroup(null)
  }

  const renameGroup = (index, name) => {
    setGroups(prev => prev.map((g, i) => i === index ? { ...g, name } : g))
  }

  const addPlayerToGroup = (groupIndex, playerId) => {
    setGroups(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, playerIds: [...g.playerIds, playerId] } : g
    ))
  }

  const removePlayerFromGroup = (groupIndex, playerId) => {
    setGroups(prev => prev.map((g, i) =>
      i === groupIndex ? { ...g, playerIds: g.playerIds.filter(id => id !== playerId) } : g
    ))
    setScorerPlayerIds(prev => prev.filter(id => id !== playerId))
  }

  const TeamBadge = ({ playerId }) => {
    const teamInfo = playerTeamMap[playerId]
    if (!teamInfo) return null
    return (
      <span style={{
        fontSize: '0.625rem',
        fontWeight: '600',
        padding: '0.0625rem 0.375rem',
        borderRadius: '999px',
        backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.12)',
        color: 'var(--accent-primary)',
        whiteSpace: 'nowrap',
      }}>
        {teamInfo.teamName}
      </span>
    )
  }

  return (
    <div>
      <div className="form-section">
        <h3>Matchups</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: '0 0 0.5rem' }}>
          Assign players to matchups (tee times). Mix players from different teams within each matchup.
        </p>
        {validSizes.length > 0 && (
          <p style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: '600', margin: '0 0 0.75rem' }}>
            Each matchup needs {validSizes.length === 1
              ? `${validSizes[0]} players`
              : `${validSizes.join(' or ')} players`
            }
          </p>
        )}
      </div>

      {/* Unassigned players banner */}
      {unassignedPlayers.length > 0 && (
        <div style={{
          padding: '0.5rem 0.75rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          marginBottom: '0.75rem',
          border: '1px solid var(--warning-color, #f59e0b)',
          textAlign: 'center',
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--warning-color, #f59e0b)' }}>
            ⚠ {unassignedPlayers.length} player{unassignedPlayers.length !== 1 ? 's' : ''} unassigned
          </span>
        </div>
      )}

      {/* Matchup cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {groups.map((group, groupIdx) => {
          const isAdding = addingToGroup === groupIdx

          return (
            <div
              key={groupIdx}
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}
            >
              {/* Matchup header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  value={group.name}
                  onChange={(e) => renameGroup(groupIdx, e.target.value)}
                  style={{
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    padding: '0.25rem 0',
                    width: '140px',
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {(() => {
                    const count = group.playerIds.length
                    const isValid = validSizes.includes(count)
                    return (
                      <span style={{
                        fontSize: '0.6875rem',
                        fontWeight: '600',
                        color: count === 0 ? 'var(--text-muted)' : isValid ? 'var(--accent-primary)' : 'var(--warning-color, #f59e0b)',
                      }}>
                        {count} player{count !== 1 ? 's' : ''} {count > 0 && (isValid ? '\u2713' : '\u26A0')}
                      </span>
                    )
                  })()}
                  {groups.length > 1 && (
                    <button
                      onClick={() => removeGroup(groupIdx)}
                      style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Players in this matchup — grouped by team */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {group.playerIds.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem 0' }}>
                    No players yet
                  </p>
                ) : (() => {
                  // Group players by team
                  const byTeam = {}
                  group.playerIds.forEach(pid => {
                    const teamInfo = playerTeamMap[pid]
                    const key = teamInfo?.teamId || '_none'
                    const label = teamInfo?.teamName || 'Unassigned'
                    const order = teamInfo?.teamOrder ?? 999
                    if (!byTeam[key]) byTeam[key] = { label, order, playerIds: [] }
                    byTeam[key].playerIds.push(pid)
                  })
                  const teamGroups = Object.values(byTeam).sort((a, b) => a.order - b.order)

                  return teamGroups.map(tg => (
                    <div key={tg.label}>
                      <div style={{
                        fontSize: '0.6875rem',
                        fontWeight: '700',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '0.125rem 0.25rem',
                        marginBottom: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <span>{tg.label}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {tg.playerIds.map(pid => {
                          const player = getPlayerByPlayerId(pid)
                          if (!player) return null
                          return (
                            <div
                              key={pid}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.375rem 0.5rem',
                                backgroundColor: 'var(--bg-tertiary)',
                                borderRadius: '0.375rem',
                              }}
                            >
                              <button
                                onClick={() => {
                                  setScorerPlayerIds(prev =>
                                    prev.includes(pid)
                                      ? prev.filter(id => id !== pid)
                                      : [...prev, pid]
                                  )
                                }}
                                title={scorerPlayerIds.includes(pid) ? 'Remove scorer' : 'Make scorer'}
                                style={{
                                  width: '26px',
                                  height: '26px',
                                  borderRadius: '9999px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: scorerPlayerIds.includes(pid)
                                    ? 'rgba(245, 158, 11, 0.2)'
                                    : 'transparent',
                                  color: scorerPlayerIds.includes(pid)
                                    ? '#f59e0b'
                                    : 'var(--text-muted)',
                                  fontSize: '0.6875rem',
                                  fontWeight: '700',
                                  flexShrink: 0,
                                }}
                              >
                                S
                              </button>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: player.avatar_color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '700',
                                fontSize: '0.625rem',
                                color: '#fff',
                                flexShrink: 0,
                              }}>
                                {getInitials(player)}
                              </div>
                              <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: '500' }}>
                                {player.display_name}
                              </span>
                              {courseHandicapMap[pid] != null && (
                                <span style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', flexShrink: 0 }}>
                                  CH: {courseHandicapMap[pid]}
                                </span>
                              )}
                              {isAdding && (
                                <button
                                  onClick={() => removePlayerFromGroup(groupIdx, pid)}
                                  style={{ color: 'var(--text-muted)', padding: '0.25rem' }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))
                })()}
              </div>

              {/* Scorer required warning */}
              {group.playerIds.length > 0 && !group.playerIds.some(pid => scorerPlayerIds.includes(pid)) && (
                <p style={{ color: 'var(--warning-color, #f59e0b)', fontSize: '0.6875rem', fontWeight: '600', textAlign: 'center', margin: '0.25rem 0 0' }}>
                  Tap the "S" to designate a scorer
                </p>
              )}

              {/* Add Players button */}
              {!isAdding && (unassignedPlayers.length > 0 || group.playerIds.length > 0) && (
                <button
                  onClick={() => setAddingToGroup(groupIdx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    padding: '0.625rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                    border: '1px solid rgba(var(--accent-primary-rgb, 14, 164, 122), 0.25)',
                    color: 'var(--accent-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                  }}
                >
                  <Users size={16} />
                  Add / Edit Players
                </button>
              )}

              {/* Expanded player selection */}
              {isAdding && (
                <div style={{
                  borderRadius: '0.5rem',
                  border: '1px solid var(--accent-primary)',
                  overflow: 'hidden',
                }}>
                  {unassignedPlayers.length > 0 && (() => {
                    // Group unassigned players by team
                    const byTeam = {}
                    unassignedPlayers.forEach(mp => {
                      const teamInfo = playerTeamMap[mp.player_id]
                      const key = teamInfo?.teamId || '_none'
                      const label = teamInfo?.teamName || 'Unassigned'
                      const order = teamInfo?.teamOrder ?? 999
                      if (!byTeam[key]) byTeam[key] = { label, order, players: [] }
                      byTeam[key].players.push(mp)
                    })
                    const teamGroups = Object.values(byTeam).sort((a, b) => a.order - b.order)

                    return (
                      <>
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                          borderBottom: '1px solid var(--border-color)',
                        }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                            Select players to add
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {teamGroups.map(tg => (
                            <div key={tg.label}>
                              <div style={{
                                padding: '0.375rem 0.75rem',
                                backgroundColor: 'var(--bg-secondary)',
                                borderBottom: '1px solid var(--border-color)',
                              }}>
                                <span style={{ fontSize: '0.6875rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {tg.label}
                                </span>
                              </div>
                              {tg.players.map(mp => {
                                const player = mp.player
                                if (!player) return null
                                return (
                                  <button
                                    key={mp.player_id}
                                    onClick={() => addPlayerToGroup(groupIdx, mp.player_id)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.625rem',
                                      padding: '0.75rem',
                                      backgroundColor: 'var(--bg-tertiary)',
                                      borderBottom: '1px solid var(--border-color)',
                                      textAlign: 'left',
                                      width: '100%',
                                    }}
                                  >
                                    <div style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      backgroundColor: player.avatar_color,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: '700',
                                      fontSize: '0.75rem',
                                      color: '#fff',
                                      flexShrink: 0,
                                    }}>
                                      {getInitials(player)}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                        {player.display_name}
                                      </span>
                                    </div>
                                    {courseHandicapMap[mp.player_id] != null && (
                                      <span style={{ fontSize: '0.6875rem', fontWeight: '600', color: 'var(--text-muted)', flexShrink: 0 }}>
                                        CH: {courseHandicapMap[mp.player_id]}
                                      </span>
                                    )}
                                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)' }}>
                                      Add
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      </>
                    )
                  })()}
                  <button
                    onClick={() => setAddingToGroup(null)}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)',
                      color: 'var(--accent-primary)',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      borderTop: unassignedPlayers.length === 0 ? 'none' : undefined,
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add Matchup button */}
      <button
        onClick={addGroup}
        style={{
          width: '100%',
          marginTop: '0.5rem',
          padding: '0.75rem',
          borderRadius: '0.5rem',
          border: '1px dashed var(--border-color)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
          fontWeight: '500',
        }}
      >
        + Add Matchup
      </button>
    </div>
  )
}

// ============================================
// Step 6: Confirm
// ============================================
function StepConfirm({ course, tee, numHoles, gameType, sessionName, groups, matchPlayers, playerTeamMap, getPlayerByPlayerId, ctpEnabled, ctpAmount, roundLowNetEnabled, roundLowNetAmount, roundSkinsEnabled, roundSkinsAmount, roundSkinsCarryOver, roundSkinsGrossTiebreaker, playerStrokesMap, scrambleTeamStrokes, courseHandicapMap, onEditMatchups }) {
  return (
    <div>
      <div className="form-section">
        <h3>Session Summary</h3>

        <div style={{
          padding: '0.75rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: '0.5rem',
          marginBottom: '0.75rem',
        }}>
          {sessionName && (
            <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.375rem' }}>
              {sessionName}
            </div>
          )}
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            {course?.name || course?.course_name || 'No course'} — {tee?.name || 'No tee'} ({numHoles} holes)
          </div>
          {gameType && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
              {gameType.name}
            </div>
          )}
        </div>

        {/* Side games summary */}
        {(ctpEnabled || roundLowNetEnabled || roundSkinsEnabled) && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '0.375rem' }}>
              Side Games
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
              {ctpEnabled && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.625rem', borderRadius: '1rem',
                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                  fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)',
                }}>
                  <Target size={12} /> CTP ${ctpAmount}
                </span>
              )}
              {roundLowNetEnabled && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.625rem', borderRadius: '1rem',
                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                  fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)',
                }}>
                  <TrendingDown size={12} /> Low Net ${roundLowNetAmount}
                </span>
              )}
              {roundSkinsEnabled && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.625rem', borderRadius: '1rem',
                  backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                  fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-primary)',
                }}>
                  $ Skins ${roundSkinsAmount}{roundSkinsCarryOver ? ' (carry)' : ''}{roundSkinsGrossTiebreaker ? ' (gross TB)' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Matchups summary */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)' }}>
            Matchups ({groups.length})
          </span>
          {onEditMatchups && (
            <button
              onClick={onEditMatchups}
              style={{
                fontSize: '0.6875rem',
                fontWeight: '600',
                color: 'var(--accent-primary)',
                padding: '0.125rem 0.5rem',
                borderRadius: '9999px',
                backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                border: 'none',
              }}
            >
              Edit
            </button>
          )}
        </div>
        <PlayerStrokesTable
          groups={groups}
          playerTeamMap={playerTeamMap}
          playerStrokesMap={playerStrokesMap}
          scrambleTeamStrokes={scrambleTeamStrokes}
          courseHandicapMap={courseHandicapMap}
          getPlayerByPlayerId={getPlayerByPlayerId}
          gameTypeId={gameType?.id}
        />
      </div>
    </div>
  )
}
